import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axiosInstance';
import { engagementAnalyticsApi, getAuthHeaders } from '../config/api';

const filterToParam = {
  'This Week': 'week',
  'This Month': 'month',
  'This Year': 'year',
};

const timeRangeParam = (activeFilter) => filterToParam[activeFilter] ?? 'month';

export const fetchEngagementHours = createAsyncThunk(
  'engagementAnalytics/fetchHours',
  async (activeFilter, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(engagementAnalyticsApi('/hours'), {
        params: { timeRange: timeRangeParam(activeFilter) },
        headers: getAuthHeaders(getState),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load engagement hours',
      );
    }
  },
);

export const fetchDirectoryBreakdown = createAsyncThunk(
  'engagementAnalytics/fetchDirectoryBreakdown',
  async ({ activeFilter, viewBy = 'department' }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(engagementAnalyticsApi('/directory/breakdown'), {
        params: { timeRange: timeRangeParam(activeFilter), viewBy },
        headers: getAuthHeaders(getState),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load directory engagement',
      );
    }
  },
);

export const fetchAiBreakdown = createAsyncThunk(
  'engagementAnalytics/fetchAiBreakdown',
  async ({ activeFilter, viewBy = 'department' }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(engagementAnalyticsApi('/ai/breakdown'), {
        params: { timeRange: timeRangeParam(activeFilter), viewBy },
        headers: getAuthHeaders(getState),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load AI engagement',
      );
    }
  },
);

export const fetchSecurityBreakdown = createAsyncThunk(
  'engagementAnalytics/fetchSecurityBreakdown',
  async ({ activeFilter = 'This Month', viewBy = 'department' }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(engagementAnalyticsApi('/security/breakdown'), {
        params: { timeRange: timeRangeParam(activeFilter), viewBy },
        headers: getAuthHeaders(getState),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load security metrics',
      );
    }
  },
);

export const fetchSupplierEngagementMetrics = createAsyncThunk(
  'engagementAnalytics/fetchSupplierEngagementMetrics',
  async (activeFilter = 'This Month', { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        engagementAnalyticsApi('/suppliers/engagement-metrics'),
        {
          params: { timeRange: timeRangeParam(activeFilter) },
          headers: getAuthHeaders(getState),
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load supplier engagement metrics',
      );
    }
  },
);

export const fetchSupplierActivityLog = createAsyncThunk(
  'engagementAnalytics/fetchSupplierActivityLog',
  async (activeFilter = 'This Month', { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(engagementAnalyticsApi('/suppliers/activity-log'), {
        params: { timeRange: timeRangeParam(activeFilter) },
        headers: getAuthHeaders(getState),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load supplier activity log',
      );
    }
  },
);

export const fetchSupplierProfile = createAsyncThunk(
  'engagementAnalytics/fetchSupplierProfile',
  async (supplierId, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        engagementAnalyticsApi(`/suppliers/${supplierId}/profile`),
        { headers: getAuthHeaders(getState) },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load supplier profile',
      );
    }
  },
);

export const fetchSupplierInteractions = createAsyncThunk(
  'engagementAnalytics/fetchSupplierInteractions',
  async ({ supplierId, activeFilter = 'This Year' }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        engagementAnalyticsApi(`/suppliers/${supplierId}/interactions`),
        {
          params: { timeRange: timeRangeParam(activeFilter) },
          headers: getAuthHeaders(getState),
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load supplier interactions',
      );
    }
  },
);

export const fetchAdminDashboardMetrics = createAsyncThunk(
  'engagementAnalytics/fetchAdminDashboard',
  async (timeRange = 'month', { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(engagementAnalyticsApi('/admin-dashboard'), {
        params: { timeRange },
        headers: getAuthHeaders(getState),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load admin dashboard',
      );
    }
  },
);

export const fetchBuyerEngagementWidgets = createAsyncThunk(
  'engagementAnalytics/fetchBuyerWidgets',
  async (activeFilter, { rejectWithValue, dispatch }) => {
    try {
      await Promise.all([
        dispatch(fetchEngagementHours(activeFilter)).unwrap(),
        dispatch(fetchDirectoryBreakdown({ activeFilter, viewBy: 'department' })).unwrap(),
        dispatch(fetchAiBreakdown({ activeFilter, viewBy: 'department' })).unwrap(),
      ]);
      return { activeFilter };
    } catch (error) {
      return rejectWithValue(
        typeof error === 'string' ? error : 'Failed to load engagement widgets',
      );
    }
  },
);

export const fetchAdminSuppliersData = createAsyncThunk(
  'engagementAnalytics/fetchAdminSuppliersData',
  async (activeFilter, { dispatch }) => {
    await Promise.all([
      dispatch(fetchSupplierEngagementMetrics(activeFilter)),
      dispatch(fetchSupplierActivityLog(activeFilter)),
    ]);
    return { activeFilter };
  },
);

const initialState = {
  hours: null,
  directoryBreakdown: [],
  aiBreakdown: [],
  securityBreakdown: [],
  securityLoading: false,
  securityError: null,
  supplierEngagementMetrics: [],
  supplierActivityLog: [],
  adminSuppliersLoading: false,
  supplierProfile: null,
  supplierInteractions: [],
  supplierDocuments: [],
  supplierDetailLoading: false,
  adminDashboard: null,
  loading: false,
  error: null,
  adminLoading: false,
  adminError: null,
};

const engagementAnalyticsSlice = createSlice({
  name: 'engagementAnalytics',
  initialState,
  reducers: {
    clearSupplierDetail(state) {
      state.supplierProfile = null;
      state.supplierInteractions = [];
      state.supplierDocuments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEngagementHours.fulfilled, (state, action) => {
        state.hours = action.payload;
      })
      .addCase(fetchDirectoryBreakdown.fulfilled, (state, action) => {
        state.directoryBreakdown = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAiBreakdown.fulfilled, (state, action) => {
        state.aiBreakdown = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSecurityBreakdown.pending, (state) => {
        state.securityLoading = true;
        state.securityError = null;
      })
      .addCase(fetchSecurityBreakdown.fulfilled, (state, action) => {
        state.securityLoading = false;
        state.securityBreakdown = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSecurityBreakdown.rejected, (state, action) => {
        state.securityLoading = false;
        state.securityError = action.payload;
      })
      .addCase(fetchSupplierEngagementMetrics.fulfilled, (state, action) => {
        state.supplierEngagementMetrics = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSupplierActivityLog.fulfilled, (state, action) => {
        state.supplierActivityLog = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAdminSuppliersData.pending, (state) => {
        state.adminSuppliersLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminSuppliersData.fulfilled, (state) => {
        state.adminSuppliersLoading = false;
      })
      .addCase(fetchAdminSuppliersData.rejected, (state, action) => {
        state.adminSuppliersLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSupplierProfile.pending, (state) => {
        state.supplierDetailLoading = true;
      })
      .addCase(fetchSupplierProfile.fulfilled, (state, action) => {
        state.supplierDetailLoading = false;
        state.supplierProfile = action.payload?.profile ?? action.payload;
        state.supplierDocuments = action.payload?.documents ?? [];
      })
      .addCase(fetchSupplierProfile.rejected, (state) => {
        state.supplierDetailLoading = false;
      })
      .addCase(fetchSupplierInteractions.pending, (state) => {
        state.supplierDetailLoading = true;
      })
      .addCase(fetchSupplierInteractions.fulfilled, (state, action) => {
        state.supplierDetailLoading = false;
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.supplierInteractions = payload;
        } else {
          state.supplierInteractions = payload?.interactions ?? [];
          if (payload?.documents) state.supplierDocuments = payload.documents;
        }
      })
      .addCase(fetchSupplierInteractions.rejected, (state) => {
        state.supplierDetailLoading = false;
      })
      .addCase(fetchBuyerEngagementWidgets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyerEngagementWidgets.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchBuyerEngagementWidgets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminDashboardMetrics.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(fetchAdminDashboardMetrics.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminDashboard = action.payload;
      })
      .addCase(fetchAdminDashboardMetrics.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });
  },
});

export const { clearSupplierDetail } = engagementAnalyticsSlice.actions;
export default engagementAnalyticsSlice.reducer;

export const selectEngagementHours = (s) => s.engagementAnalytics.hours;
export const selectDirectoryBreakdown = (s) => s.engagementAnalytics.directoryBreakdown;
export const selectAiBreakdown = (s) => s.engagementAnalytics.aiBreakdown;
export const selectSecurityBreakdown = (s) => s.engagementAnalytics.securityBreakdown;
export const selectSecurityLoading = (s) => s.engagementAnalytics.securityLoading;
export const selectSecurityError = (s) => s.engagementAnalytics.securityError;
export const selectSupplierEngagementMetrics = (s) =>
  s.engagementAnalytics.supplierEngagementMetrics;
export const selectSupplierActivityLog = (s) => s.engagementAnalytics.supplierActivityLog;
export const selectAdminSuppliersLoading = (s) => s.engagementAnalytics.adminSuppliersLoading;
export const selectSupplierProfile = (s) => s.engagementAnalytics.supplierProfile;
export const selectSupplierInteractions = (s) => s.engagementAnalytics.supplierInteractions;
export const selectSupplierDocuments = (s) => s.engagementAnalytics.supplierDocuments;
export const selectSupplierDetailLoading = (s) => s.engagementAnalytics.supplierDetailLoading;
export const selectAdminDashboardMetrics = (s) => s.engagementAnalytics.adminDashboard;
export const selectEngagementLoading = (s) => s.engagementAnalytics.loading;
export const selectEngagementError = (s) => s.engagementAnalytics.error;
export const selectAdminDashboardLoading = (s) => s.engagementAnalytics.adminLoading;
export const selectAdminDashboardError = (s) => s.engagementAnalytics.adminError;
